import React from 'react';
import { Paper, Text, Group, Badge, Stack } from '@mantine/core';
import ReportLink from '../Reports/ReportLink';

/**
 * PastCompetitionCard
 *
 * One card per past-season registration: the year, the dancer, and one
 * sub-card per variation performed (category + age division). Each variation
 * carries its own "View Report" button, which reads `registration.judging_reports`
 * (RLS → published rows only) and shows "Pending" until results are released.
 *
 * Deliberately no rank / Top-N here — that lives on the report itself.
 * Mobile-first: everything stacks; no table.
 */

const STATUS_BADGE = {
  attending: { label: 'Competed',        color: 'green'  },
  no_show:   { label: 'Did not perform', color: 'gray'   },
  withdrew:  { label: 'Withdrew',        color: 'gray'   },
  deferred:  { label: 'Deferred',        color: 'yellow' },
};

const PastCompetitionCard = ({ registration }) => {
  const di = registration.dancer_information || {};
  const name = `${di.first_name || ''} ${di.last_name || ''}`.trim();
  const status = STATUS_BADGE[registration.participation_status] || STATUS_BADGE.attending;
  const competed = registration.participation_status === 'attending';
  const regNumber = registration.registration_number
    || `PDC${registration.competition_year}-${registration.id.substring(0, 8).toUpperCase()}`;

  return (
    <Paper radius="md" p="lg" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>
      <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
        <div style={{ textAlign: 'left' }}>
          <Text
            component="div"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.25rem',
              fontWeight: 700,
              color: 'var(--charcoal)',
              lineHeight: 1.3,
              margin: 0,
            }}
          >
            Prix du Canada {registration.competition_year}
          </Text>
          <Text size="sm" c="dimmed" style={{ margin: 0 }}>
            {name}{regNumber ? ` · ${regNumber}` : ''}
          </Text>
        </div>
        <Badge color={status.color} variant="light" size="md">{status.label}</Badge>
      </Group>

      <Stack gap="sm" mt="md">
        {(registration.variations || []).map((v) => (
          <div
            key={v.id}
            style={{
              padding: '0.75rem 0.9rem',
              background: 'var(--soft-cream, #faf7f0)',
              borderRadius: '8px',
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
              <div style={{ textAlign: 'left', minWidth: 0 }}>
                <Text size="sm" fw={600} style={{ color: 'var(--charcoal)' }}>
                  {v.variation_name}
                </Text>
                {(v.ballet_origin || v.character_name) && (
                  <Text size="xs" c="dimmed">
                    {v.ballet_origin ? <>from <em>{v.ballet_origin}</em></> : null}
                    {v.ballet_origin && v.character_name ? ' · ' : ''}
                    {v.character_name || ''}
                  </Text>
                )}
              </div>
              {competed && <ReportLink registration={registration} variationId={v.id} size="sm" />}
            </Group>
            <Group gap="xs" mt="xs" wrap="wrap">
              {v.performance_category && (
                <Badge size="sm" color="yellow" variant="light">{v.performance_category}</Badge>
              )}
              {v.age_division && (
                <Badge size="sm" color="gray" variant="light">{v.age_division}</Badge>
              )}
            </Group>
          </div>
        ))}
      </Stack>
    </Paper>
  );
};

export default PastCompetitionCard;
